import { endOfMonth, Interval, startOfMonth } from 'date-fns'
import { FC, useMemo } from 'react'

import { useGetEventsForTrackedGamesQuery } from '../../../../api/core'
import { DateRangeValue } from '../../../../components/DateRangePicker/DateRangePicker'
import { useCurrentMarket } from '../../../markets'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEventByGame } from '../../types/TrackingEvents'
import { LiveEventInstanceTableContainer } from '../LiveEventInstanceTable/LiveEventInstanceTableContainer'

type PerformanceDialogEventsContainerProps = {
  appId: number
  dateRange?: DateRangeValue
  interval?: Interval
  trackedGames?: TrackedGame[]
  parentEvents: TrackingEventByGame
  isLoading?: boolean
  onEventHighlighted?: (eventId: string) => void
}

/**
 * Fetches live events for the performance dialog's current date range,
 * falling back to parent-provided events when the date range is not available.
 * This ensures the events table shows data even when the user extends the
 * modal's timespan beyond the page's loaded data range.
 */
export const PerformanceDialogEventsContainer: FC<PerformanceDialogEventsContainerProps> = ({
  appId,
  dateRange,
  interval,
  trackedGames,
  parentEvents,
  isLoading,
  onEventHighlighted,
}) => {
  const { currentMarketIso } = useCurrentMarket()
  const gameId = trackedGames?.find((tg) => tg.game.appId === appId)?.game.id

  const queryRange = useMemo(() => {
    if (!dateRange?.fromDate || !dateRange?.toDate) return null
    return {
      startTimestamp: startOfMonth(dateRange.fromDate).getTime(),
      endTimestamp: endOfMonth(dateRange.toDate).getTime(),
    }
  }, [dateRange?.fromDate, dateRange?.toDate])

  const shouldFetch = !!gameId && !!queryRange
  const eventsQuery = useGetEventsForTrackedGamesQuery(
    {
      gameIds: gameId ? [gameId] : [],
      startTimestamp: queryRange?.startTimestamp ?? 0,
      endTimestamp: queryRange?.endTimestamp ?? 0,
      marketIso: currentMarketIso,
    },
    { skip: !shouldFetch }
  )

  const events = shouldFetch && eventsQuery.data ? eventsQuery.data.events : parentEvents

  return (
    <LiveEventInstanceTableContainer
      interval={interval}
      events={events}
      trackedGames={trackedGames}
      appId={appId}
      isLoading={isLoading || eventsQuery.isFetching}
      onEventHighlighted={onEventHighlighted}
    />
  )
}
